import React, { useState } from 'react';
import { useData } from 'src/contexts/DataContext';
import {
  PrinterTitle,
  PrinterThanksButton,
  PrinterCurrentPlan,
  PrinterPagesPerMonth,
  PrinterButton,
  PrinterCommitmentDetails,
  PrinterDescription,
  PrinterQuickLink,
  PrinterTotalPermonth,
  CheckmarkText,
  PrinterDivider,
  ActionContainer,
  PrinterMainTitle
} from './styles';
import { IconCheckmark, IconPrinterInkDrop } from '@veneer/core';
import AutomaticFirmwareUpdatesModal from '../../AutomaticFirmwareUpdatesModal';
import { PrinterItem } from 'src/utils/Products/productFilter';
import { useTranslationWithInterpolation } from 'src/hooks/useTranslationWithValues/useTranslationWithValuesHelper';
import DOMPurify from 'dompurify';
import {
  calculatePriceDifference,
  formatPrice
} from 'src/utils/Products/getCalculatePriceDifferance';

interface RecommendedPrinterProps {
  nonCurrentPrinterDetails?: PrinterItem[];
  currentPrinterDetails?: PrinterItem[];
}

const RecommendedPrinterComponent = ({
  nonCurrentPrinterDetails,
  currentPrinterDetails
}: RecommendedPrinterProps): JSX.Element => {
  const dataContext = useData();
  const { selectedPrinterSku } = dataContext || {};
  const recommendedPrinter = nonCurrentPrinterDetails?.find(
    (p: PrinterItem) => p.recommendedPrinter === true
  );

  const recommendedPlan = recommendedPrinter?.inkPlan.find(
    (plan) => plan.recommendedPlan === true
  );

  const { increaseDecreaseAmount, increaseOrDecrease } =
    calculatePriceDifference(
      recommendedPlan?.totalPrice,
      currentPrinterDetails[0]?.inkPlan[0].totalPrice
    );
  let planNote = '';
  const { getText, getTextWithValues } = useTranslationWithInterpolation();

  const [showFirmwareModal, setShowFirmwareModal] = useState(false);
  const planType = recommendedPrinter?.currentPrinter;

  const htmlString = recommendedPrinter?.shortDescription?.html || '';
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, 'text/html');
  const showDescriptionText = doc.body.textContent || '';

  if (planType) {
    planNote = DOMPurify.sanitize(
      getText(
        'printerDetails.currentPlanInfo',
        'This is your current print plan.'
      )
    );
  } else {
    planNote = DOMPurify.sanitize(
      getTextWithValues(
        'printerDetails.currentPlan',
        'Your current plan is {{printerModel}} printer with {{pages}} pages/month. <br/> <b> Plan fee will {{difference}} by only {{charges}}/month</b>',
        {
          printerModel: currentPrinterDetails[0]?.printerName,
          pages: currentPrinterDetails[0]?.inkPlan[0]?.pagesPerMonth,
          difference: increaseOrDecrease,
          charges: `$${increaseDecreaseAmount}`
        }
      )
    );
  }

  const handleSelectPrinter = () => {
    setShowFirmwareModal(true);
  };

  const handleCloseFirmwareModal = () => {
    setShowFirmwareModal(false);
  };

  const handleProceedFirmware = () => {
    setShowFirmwareModal(false);
    if (selectedPrinterSku && recommendedPrinter) {
      selectedPrinterSku(recommendedPrinter.productSKU);
    }
  };

  return (
    <div>
      <PrinterMainTitle>{recommendedPrinter?.modelPurpose}</PrinterMainTitle>
      <PrinterTitle>{recommendedPrinter?.printerName}</PrinterTitle>
      <PrinterDescription>{showDescriptionText}</PrinterDescription>
      <PrinterQuickLink
        href={recommendedPrinter?.fullspecification}
        target="_blank"
      >
        {getText('printerDetails.seeAllSpecs', 'See all specs')}
      </PrinterQuickLink>
      <PrinterDivider />
      <PrinterCommitmentDetails>
        {getTextWithValues(
          'printerDetails.commitmentDetails',
          '{{years}} years commitment',
          {
            years: recommendedPlan?.commitmentPeriod
          }
        )}
      </PrinterCommitmentDetails>
      <PrinterTotalPermonth>
        {getTextWithValues(
          'printerDetails.totalPerMonth',
          'Total {{amount}}/month',
          {
            amount: formatPrice(Math.abs(recommendedPlan?.totalPrice))
          }
        )}
      </PrinterTotalPermonth>
      <PrinterPagesPerMonth>
        <IconCheckmark size={20} />{' '}
        <CheckmarkText>
          {getTextWithValues(
            'printerDetails.includesPages',
            'Includes {{pages}} pages/month',
            {
              pages: recommendedPlan?.pagesPerMonth
            }
          )}
        </CheckmarkText>
      </PrinterPagesPerMonth>
      <PrinterCurrentPlan>
        <IconPrinterInkDrop size={20} />
        <div dangerouslySetInnerHTML={{ __html: planNote }}></div>
      </PrinterCurrentPlan>
      <ActionContainer>
        <PrinterButton
          onClick={handleSelectPrinter}
          appearance="primary"
          data-testid="printerSelectButton"
        >
          {getText('printerDetails.selectPrinter', 'Select this printer')}
        </PrinterButton>
        <PrinterThanksButton appearance="ghost">
          {getText('printerDetails.upgradeLater', 'Upgrade later')}
        </PrinterThanksButton>
      </ActionContainer>

      {/* Automatic Firmware Updates Modal */}
      <AutomaticFirmwareUpdatesModal
        show={showFirmwareModal}
        onClose={handleCloseFirmwareModal}
        onProceed={handleProceedFirmware}
      />
    </div>
  );
};

export default RecommendedPrinterComponent;
